// assets/product-custom-property.js
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 * @property {HTMLElement} [selectedValue] - The selected swatch value label.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#updateCharacterCount();
    this.#updateSelectedSwatchValue();
    this.#setupCategorySwatchFiltering();
    this.#setupConditionalVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  handleInput() {
    this.#updateCharacterCount();
  }

  handleSwatchChange() {
    this.#updateSelectedSwatchValue();
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;

    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }

  #updateSelectedSwatchValue() {
    const { selectedValue } = this.refs;
    if (!(selectedValue instanceof HTMLElement)) return;

    const emptyValue = selectedValue.dataset.emptyValue || 'None selected';

    const checkedInput = this.querySelector('input[type="radio"][data-swatch-label]:checked');
    if (!(checkedInput instanceof HTMLInputElement)) {
      selectedValue.textContent = emptyValue;
      return;
    }

    selectedValue.textContent = checkedInput.dataset.swatchLabel || checkedInput.value;
  }

  #setupConditionalVisibility() {
    const conditionalElements = Array.from(this.querySelectorAll('[data-show-for-option-value]')).filter(
      (node) => node instanceof HTMLElement
    );
    if (!conditionalElements.length) return;

    const { signal } = this.#abortController;
    const sectionRoot = this.closest('.shopify-section') || document;

    const syncVisibility = () => {
      conditionalElements.forEach((conditionalElement) => {
        this.#syncConditionalVisibility(conditionalElement);
      });
    };

    sectionRoot.addEventListener(ThemeEvents.variantUpdate, syncVisibility, { signal });
    sectionRoot.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (!target.closest('variant-picker')) return;
        syncVisibility();
      },
      { signal }
    );

    syncVisibility();
  }

  #setupCategorySwatchFiltering() {
    const categorySelects = Array.from(this.querySelectorAll('[data-category-select]')).filter(
      (node) => node instanceof HTMLSelectElement
    );

    if (!categorySelects.length) return;

    const { signal } = this.#abortController;

    categorySelects.forEach((categorySelect) => {
      categorySelect.addEventListener(
        'change',
        () => {
          const container = categorySelect.closest('.__categorized-select');
          if (!(container instanceof HTMLElement)) return;
          this.#syncCategorySwatches(container);
          this.#updateSelectedSwatchValue();
        },
        { signal }
      );
    });

    this.#syncAllCategorySwatches();
  }

  #syncAllCategorySwatches() {
    const categorizedContainers = Array.from(this.querySelectorAll('.__categorized-select')).filter(
      (node) => node instanceof HTMLElement
    );

    categorizedContainers.forEach((container) => {
      this.#syncCategorySwatches(container);
    });
  }

  /**
   * @param {HTMLElement} container
   */
  #syncCategorySwatches(container) {
    const categorySelect = container.querySelector('[data-category-select]');
    const swatchContainer = container.querySelector('[data-category-swatches]');

    if (!(categorySelect instanceof HTMLSelectElement) || !(swatchContainer instanceof HTMLElement)) return;

    const selectedCategory = this.#normalizeValue(categorySelect.value || '');
    const isContainerVisible = !container.hidden;
    const requireWhenVisible = container.dataset.requireWhenVisible === 'true';

    const swatchLabels = Array.from(swatchContainer.querySelectorAll('[data-color-category]')).filter(
      (node) => node instanceof HTMLElement
    );

    let visibleSwatchCount = 0;

    swatchLabels.forEach((label) => {
      const labelCategory = this.#normalizeValue(label.dataset.colorCategory || '');
      const shouldShow = Boolean(selectedCategory) && labelCategory === selectedCategory;
      const input = label.querySelector('input[type="radio"]');

      label.hidden = !shouldShow;
      label.classList.toggle('is-hidden', !shouldShow);

      if (!(input instanceof HTMLInputElement)) return;

      const shouldEnable = isContainerVisible && shouldShow;
      input.disabled = !shouldEnable;

      if (!shouldEnable && input.checked) {
        input.checked = false;
      }

      if (!shouldEnable) {
        input.required = false;
        input.removeAttribute('aria-required');
      }

      if (shouldEnable) {
        visibleSwatchCount += 1;
      }
    });

    swatchContainer.hidden = !(isContainerVisible && Boolean(selectedCategory));

    const shouldRequireSelection = isContainerVisible && Boolean(selectedCategory) && requireWhenVisible && visibleSwatchCount > 0;
    const visibleInputs = Array.from(swatchContainer.querySelectorAll('input[type="radio"]'));

    visibleInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement) || input.disabled) return;

      input.required = shouldRequireSelection;

      if (shouldRequireSelection) {
        input.setAttribute('aria-required', 'true');
      } else {
        input.removeAttribute('aria-required');
      }
    });
  }

  /**
   * @param {HTMLElement} conditionalElement
   */
  #syncConditionalVisibility(conditionalElement) {
    if (!(conditionalElement instanceof HTMLElement)) return;

    const triggerValue = this.#normalizeValue(conditionalElement.dataset.showForOptionValue || '');
    if (!triggerValue) return;

    const optionIndexRaw = conditionalElement.dataset.showForOptionIndex || '';
    const optionIndex = Number.parseInt(optionIndexRaw, 10);

    const selectedValues = this.#getSelectedVariantValues(
      conditionalElement.dataset.productId,
      Number.isNaN(optionIndex) ? undefined : optionIndex - 1
    ).map((value) => this.#normalizeValue(value));
    const shouldShow = selectedValues.some((value) => value === triggerValue || value.includes(triggerValue));
    const hasGiantPrintToOrder = selectedValues.some(
      (value) => value.includes('giantprinttoorder') || (value.includes('giant') && value.includes('printtoorder'))
    );
    const requireWhenVisible = conditionalElement.dataset.requireWhenVisible === 'true';
    const formControls = Array.from(conditionalElement.querySelectorAll('input, select, textarea'));

    conditionalElement.hidden = !shouldShow;

    // Also toggle the print-to-order note if present
    const { printToOrderNote, conditionalHeading } = this.refs;
    if (printToOrderNote instanceof HTMLElement) {
      printToOrderNote.hidden = !shouldShow;
    }

    if (conditionalHeading instanceof HTMLElement) {
      const defaultLabel = conditionalHeading.dataset.defaultLabel || conditionalHeading.textContent || '';
      const giantLabel = conditionalHeading.dataset.giantLabel || defaultLabel;

      conditionalHeading.textContent = hasGiantPrintToOrder ? giantLabel : defaultLabel;
    }

    formControls.forEach((control) => {
      if (
        !(
          control instanceof HTMLInputElement ||
          control instanceof HTMLSelectElement ||
          control instanceof HTMLTextAreaElement
        )
      ) {
        return;
      }

      control.disabled = !shouldShow;
      control.required = shouldShow && requireWhenVisible;

      if (control.required) {
        control.setAttribute('aria-required', 'true');
      } else {
        control.removeAttribute('aria-required');
      }
    });

    this.#syncAllCategorySwatches();
    this.#updateSelectedSwatchValue();
  }

  /**
  * @param {string | undefined} productId
  * @param {number | undefined} optionIndex
   * @returns {string[]}
   */
  #getSelectedVariantValues(productId, optionIndex) {
    const sectionRoot = this.closest('.shopify-section') || document;
    const pickersInSection = Array.from(sectionRoot.querySelectorAll('variant-picker')).filter(
      (node) => node instanceof HTMLElement
    );

    const variantPicker =
      (productId ? pickersInSection.find((picker) => picker.dataset.productId === productId) : null) ||
      pickersInSection.find((picker) => picker.dataset.templateProductMatch === 'true') ||
      pickersInSection[0] ||
      document.querySelector('variant-picker[data-template-product-match="true"], variant-picker');

    if (!(variantPicker instanceof HTMLElement)) return [];

    if (optionIndex !== undefined && optionIndex >= 0) {
      const checkedInput = variantPicker.querySelector(`input[type="radio"][data-fieldset-index="${optionIndex}"]:checked`);
      if (checkedInput instanceof HTMLInputElement) {
        return [checkedInput.value];
      }

      const selects = Array.from(variantPicker.querySelectorAll('select'));
      const selectedSelect = selects[optionIndex];
      if (selectedSelect instanceof HTMLSelectElement) {
        return [selectedSelect.value];
      }
    }

    const checkedRadioValues = Array.from(variantPicker.querySelectorAll('input[type="radio"]:checked'))
      .map((input) => (input instanceof HTMLInputElement ? input.value : ''))
      .filter(Boolean);

    const selectedDropdownValues = Array.from(variantPicker.querySelectorAll('select'))
      .map((select) => (select instanceof HTMLSelectElement ? select.value : ''))
      .filter(Boolean);

    return [...checkedRadioValues, ...selectedDropdownValues];
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeValue(value) {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '');
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
